import React, { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { db, storage } from "@lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  orderBy,
  getDocs as getDocsFromCollection,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { InputFieldProfile } from "../common/InputFieldProfile";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserEdit,
  faCamera,
  faLock,
  faEye,
  faEdit,
  faTrash,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Timestamp } from "firebase/firestore";

export const Profile = ({ labelColor = "text-slate-800" }) => {
  const { user, logout } = useAuth();
  const { showNotification, showConfirm } = useNotification();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    bibName: "",
    email: "",
    telepon: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    golonganDarah: "",
    sizeJersey: "",
    gender: "",
    negara: "",
    provinsi: "",
    kota: "",
    alamat: "",
    kontakDarurat: "",
    namaKontakDarurat: "",
    hubunganKontakDarurat: "",
  });
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState(() => {
    return localStorage.getItem("activeMenu") || "Edit Profile";
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [stravaData, setStravaData] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    localStorage.setItem("activeMenu", activeMenu);
  }, [activeMenu]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        if (isLoggingOut || !user) {
          return;
        }

        if (! user.emailVerified) {
          await logout();
          navigate("/login");
          return;
        }

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (! isMounted) return;

        if (docSnap.exists()) {
          const data = docSnap.data();
          const fullName = data.namaLengkap || user.displayName || "";
          const [namaDepan, ... namaBelakang] = fullName.split(" ");
          setFormData({
            ... data,
            namaDepan,
            namaBelakang: namaBelakang.join(" "),
          });
          if (data.strava) {
            setStravaData(data.strava);
          }
        } else {
          const fullName = user.displayName || "";
          const [namaDepan, ...namaBelakang] = fullName.split(" ");
          setFormData({
            namaDepan,
            namaBelakang: namaBelakang.join(" "),
            email: user.email,
            bibName: "",
            telepon: "",
            nomorIdentitas: "",
            tanggalLahir: "",
            golonganDarah: "",
            sizeJersey: "",
            gender: "",
            negara: "",
            provinsi: "",
            kota: "",
            alamat: "",
            kontakDarurat: "",
            namaKontakDarurat: "",
            hubunganKontakDarurat: "",
          });
          console.log(
            "Data user belum ada di Firestore, menggunakan displayName"
          );
        }

        } catch (err) {
        if (!isMounted || isLoggingOut) return;
        console.error("Gagal mengambil data profil:", err);
        showNotification("error", "Failed to fetch user data.");
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user, logout, navigate, showNotification, isLoggingOut]);

  const handleConnectStrava = () => {
    const clientId = "156089";
    const redirectUri = "https://cakrawalajakarta.id/strava-callback";
    const scope = "read,activity:read_all,profile:read_all";
    const authUrl = `https://www.strava.com/oauth/authorize? client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=code&scope=${encodeURIComponent(
      scope
    )}&approval_prompt=auto`;

    window.location.href = authUrl;
  };

  const handleDisconnectStrava = async () => {
    showConfirm(
      "Are you sure you want to disconnect from Strava?",
      async () => {
        setLoading(true);
        try {
          await updateDoc(doc(db, "users", user.uid), {
            strava: null,
          });
          setStravaData(null);
          setShowStats(false);
          showNotification("success", "Strava connection disconnected successfully!");
        } catch (err) {
          console.error("Gagal memutuskan koneksi Strava:", err);
          showNotification(
            "error",
            "Failed to disconnect Strava. Please try again!"
          );
        } finally {
          setLoading(false);
        }
      },
      "Disconnect",
      "Cancel"
    );
  };

  const toggleShowStats = () => {
    setShowStats((prev) => !prev);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e. target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fullName = `${formData.namaDepan} ${formData.namaBelakang}`. trim();
      if (! formData.namaDepan || !formData.namaBelakang) {
        showNotification(
          "error",
          "First name and last name cannot be empty!"
        );
        setLoading(false);
        return;
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          ... formData,
          namaLengkap: fullName,
          email: user.email,
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );

      const eventsSnapshot = await getDocsFromCollection(
        collection(db, "events")
      );
      let updatedRegistrations = 0;

      const registrationData = {
        namaLengkap: fullName,
        namaDepan: formData.namaDepan,
        namaBelakang: formData.namaBelakang,
        bibName: formData.bibName,
        email: formData.email,
        telepon: formData.telepon,
        nomorIdentitas: formData.nomorIdentitas,
        tanggalLahir: formData.tanggalLahir,
        golonganDarah: formData.golonganDarah,
        sizeJersey: formData.sizeJersey,
        gender: formData.gender,
        negara: formData.negara,
        provinsi: formData.provinsi,
        kota: formData.kota,
        alamat: formData.alamat,
        kontakDarurat: formData.kontakDarurat,
        namaKontakDarurat: formData.namaKontakDarurat,
        hubunganKontakDarurat: formData.hubunganKontakDarurat,
        updatedAt: Timestamp.now(),
      };

      for (const eventDoc of eventsSnapshot.docs) {
        const { collectionName } = eventDoc.data();
        const registrationQuery = query(
          collection(db, collectionName),
          where("uid", "==", user. uid)
        );
        const registrationSnapshot = await getDocs(registrationQuery);

        if (! registrationSnapshot.empty) {
          for (const registrationDoc of registrationSnapshot.docs) {
            await updateDoc(registrationDoc.ref, registrationData);
            updatedRegistrations++;
            console.log(
              `Berhasil memperbarui pendaftaran di ${collectionName} untuk orderId=${registrationDoc.id}`
            );
          }
        }
      }

      if (updatedRegistrations > 0) {
        showNotification(
          "success",
          "Profile updated successfully!"
        );
      } else {
        showNotification("success", "Profile updated successfully!");
      }
    } catch (err) {
      console.error("Gagal menyimpan data:", err);
      showNotification("error", "An error occurred while saving data!");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (! file.type.startsWith("image/")) {
        showNotification("error", "File must be an image (jpg, png, etc.)!");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showNotification("error", "Maximum image size is 5MB!");
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoSubmit = async (e) => {
    e. preventDefault();
    if (!photoFile) {
      showNotification("error", "Please select an image first!");
      return;
    }

    setLoading(true);
    try {
      const storageRef = ref(storage, `profilePictures/${user.uid}`);
      await uploadBytes(storageRef, photoFile);
      const photoURL = await getDownloadURL(storageRef);

      await updateProfile(user, { photoURL });

      await updateDoc(doc(db, "users", user.uid), { photoURL });

      showNotification("success", "Profile photo updated successfully!");
      setPhotoFile(null);
      setPhotoPreview(null);
    } catch (err) {
      console.error("Gagal mengunggah foto profil:", err);
      showNotification(
        "error",
        "Failed to upload profile photo. Please try again!"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCancel = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (! passwordData.currentPassword) {
        showNotification("error", "Enter old password!");
        setLoading(false);
        return;
      }
      if (passwordData.newPassword. length < 6) {
        showNotification("error", "New password must be at least 6 characters!");
        setLoading(false);
        return;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        showNotification(
          "error",
          "New password and confirmation do not match!"
        );
        setLoading(false);
        return;
      }

      const credential = EmailAuthProvider.credential(
        user. email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, passwordData.newPassword);

      await updateDoc(doc(db, "users", user.uid), {
        lastPasswordChange: Timestamp.now(),
      });

      showNotification("success", "Password updated successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error("Gagal memperbarui kata sandi:", err);
      if (err.code === "auth/wrong-password") {
        showNotification("error", "Incorrect old password!");
      } else if (err.code === "auth/too-many-requests") {
        showNotification("error", "Too many attempts.  Try again later!");
      } else {
        showNotification(
          "error",
          "Failed to update password. Please try again!"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const isGoogleLogin = user?.providerData. some(
    (provider) => provider.providerId === "google.com"
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    return `${date.getDate(). toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()} ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date. getMinutes().toString().padStart(2, "0")}`;
  };

  const menus = [
    { name: "Edit Profile", icon: faUserEdit },
    { name: "Change Profile Photo", icon: faCamera },
    { name: "Change Password", icon: faLock },
    { name: "Logout", icon: faSignOutAlt },
  ];

  const handleLogoutMenu = () => {
    showConfirm(
      "Are you sure you want to logout?",
      async () => {
        setIsLoggingOut(true);
        try {
          await logout();
          navigate("/login");
        } catch (err) {
          setIsLoggingOut(false);
          console.error("Gagal logout:", err);
          showNotification("error", "Failed to logout. Please try again.");
        }
      },
      "Logout",
      "Cancel"
    );
  };

  const renderContent = () => {
    switch (activeMenu) {
      case "Edit Profile":
        return (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">
              Edit JOTR Profile
            </h2>
            <div className="flex justify-center mb-4">
              <img
                src={
                  user?. photoURL ||
                  "https://www.gravatar.com/avatar/00000000000000000000000000000000? d=mp&f=y"
                }
                alt="Avatar"
                className="w-24 h-24 rounded-full border-4 border-gray-200"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://www. gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
                }}
              />
            </div>
            <p className="text-lg mb-6 text-center text-slate-800">
              Hello,{" "}
              <strong>
                {`${formData.namaDepan} ${formData.namaBelakang}` ||
                  user?.displayName ||
                  user. email}
              </strong>
            </p>
            <form onSubmit={handleSubmit}>
              <InputFieldProfile
                formData={formData}
                handleChange={handleChange}
                isFormDisabled={loading}
                labelColor={labelColor}
              />
              <div className="flex justify-center mt-6">
                <Button
                  type="submit"
                  loading={loading}
                  className="w-full max-w-xs"
                >
                  Save Data
                </Button>
              </div>
            </form>
          </div>
        );
      case "Change Profile Photo":
        return (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">
              Change Profile Photo
            </h2>
            <div className="flex justify-center mb-4">
              <img
                src={
                  photoPreview ||
                  user?.photoURL ||
                  "https://www.gravatar.com/avatar/00000000000000000000000000000000? d=mp&f=y"
                }
                alt="Avatar Preview"
                className="w-32 h-32 rounded-full border-4 border-gray-200 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
                }}
              />
            </div>
            <form onSubmit={handlePhotoSubmit}>
              <InputField
                label="Select Image"
                name="photo"
                type="file"
                onChange={handlePhotoChange}
                accept="image/*"
                disabled={loading}
                labelColor="text-slate-800"
              />
              <div className="flex justify-center gap-4 mt-6">
                <Button
                  type="submit"
                  loading={loading}
                  className="w-full max-w-[140px]"
                >
                  Upload
                </Button>
                {photoFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handlePhotoCancel}
                    className="w-full max-w-[140px] border border-slate-200"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </div>
        );
      case "Change Password":
        return (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">
              Change Password
            </h2>
            {isGoogleLogin ? (
              <p className="text-gray-600">
                Your account uses Google Login. Password is managed by
                Google. 
              </p>
            ) : (
              <form onSubmit={handlePasswordSubmit}>
                <InputField
                  label="Old Password"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter old password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <InputField
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <InputField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData. confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <div className="flex justify-center gap-4 mt-6">
                  <Button
                    type="submit"
                    loading={loading}
                    className="w-full max-w-[140px]"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handlePasswordCancel}
                    className="w-full max-w-[140px] border border-slate-200"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section className="min-h-screen bg-white px-4 py-20">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-64 bg-white rounded-2xl border border-slate-200 p-4 block">
            <ul>
              {menus.map((menu) => (
                <li key={menu. name}>
                  <button
                    onClick={() => {
                      if (menu.name === "Logout") {
                        handleLogoutMenu();
                      } else {
                        setActiveMenu(menu. name);
                      }
                    }}
                    className={`w-full flex items-center gap-2 px-3 h-10 mb-1 text-md font-medium rounded-xl transition-colors duration-200 overflow-hidden ${
                      activeMenu === menu.name
                        ?  "bg-red-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <FontAwesomeIcon icon={menu. icon} className="flex-shrink-0" />
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                      {menu.name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1">{renderContent()}</div>
        </div>
      </div>
    </section>
  );
};

export default Profile;